/**
 * Custom React hook for non-streaming RAG chat functionality
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setIsStreaming } from '../store/chatModeSlice';
import { JiraChatService, JiraResponse } from '../utils/rag/jira.service';
import type { ChatMessage as CoreChatMessage } from '../utils/chat/chat.service';
import logger from '../utils/logger/logger';

/** Generate a simple unique session ID for a Jira conversation */
function generateJiraSessionId(): string {
  const rand =
    typeof window !== 'undefined' && window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint8Array(5)), b => b.toString(36).padStart(2, '0')).join('').substring(0, 9)
      : Math.random().toString(36).substring(2, 11);
  return `jira_${Date.now()}_${rand}`;
}

export type ChatMessage = CoreChatMessage;

interface UseNonStreamingRagChatState {
  chatHistory: ChatMessage[];
  currentResponse: string;
  isStreaming: boolean;
  error: string | null;
}

interface UseNonStreamingRagChatReturn extends UseNonStreamingRagChatState {
  sendMessage: (message: string, onComplete?: () => void) => Promise<void>;
  cancelRequest: () => void;
  clearHistory: () => void;
}

/**
 * Custom hook for non-streaming RAG chat functionality (e.g., JIRA)
 */
export function useNonStreamingRagChat(options?: {
  onError?: (error: Error) => void;
}): UseNonStreamingRagChatReturn {
  const dispatch = useDispatch();
  const [state, setState] = useState<UseNonStreamingRagChatState>({
    chatHistory: [],
    currentResponse: '',
    isStreaming: false,
    error: null,
  });

  const serviceRef = useRef<JiraChatService | null>(null);
  // Jira-specific session ID — persists for the lifetime of the conversation,
  // reset when clearHistory() is called (mirrors how sessionId works in useChatStream).
  const jiraSessionIdRef = useRef<string>('');

  /** Return (and lazily create) the current Jira session ID */
  const getOrCreateJiraSessionId = useCallback((): string => {
    if (!jiraSessionIdRef.current) {
      jiraSessionIdRef.current = generateJiraSessionId();
      logger.log('[JIRA HOOK] New Jira session ID generated:', jiraSessionIdRef.current);
    }
    return jiraSessionIdRef.current;
  }, []);

  // Lazy-initialize service only when first needed, not on every ChatPage render.
  const getService = useCallback(() => {
    if (!serviceRef.current) {
      serviceRef.current = new JiraChatService();
      logger.log('[JIRA HOOK] Service initialized');
    }
    return serviceRef.current;
  }, []);

  /**
   * Send a message to Jira RAG backend
   */
  const sendMessage = useCallback(async (message: string, onComplete?: () => void) => {
    if (!message.trim()) {
      logger.warn('[JIRA HOOK] Attempted to send empty message');
      return;
    }

    const service = getService();

    try {
      // Add user message to chat history
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: message,
        timestamp: Date.now(),
      };

      setState(prev => ({
        ...prev,
        chatHistory: [...prev.chatHistory, userMessage],
        error: null,
        isStreaming: true,
        currentResponse: '',
      }));
      dispatch(setIsStreaming(true));

      logger.log('[JIRA HOOK] Sending message:', message);

      // Send query to backend, passing the persistent session ID for this conversation
      const sessionId = getOrCreateJiraSessionId();
      const response: JiraResponse | { __cancelled?: boolean } | undefined = await service.sendQuery(message, sessionId);

      // If response is the special cancellation object, exit gracefully
      if (response && typeof response === 'object' && '__cancelled' in response) {
        setState(prev => ({
          ...prev,
          isStreaming: false,
          currentResponse: '',
        }));
        dispatch(setIsStreaming(false));
        if (onComplete) {
          onComplete();
        }
        return;
      }

      // If response is undefined/null for any other reason, treat as error
      if (!response) {
        throw new Error('No response received from Jira backend');
      }

      logger.log('[JIRA HOOK] Received raw response:', response);
      logger.log('[JIRA HOOK] Response details:', {
        sessionId: response.chat_id,
        answerLength: response.answer.length,
        logsCount: response.logs.length,
        ui_events: response.ui_events,
        metadata: response.metadata,
      });

      // Create assistant message with the markdown answer and ui_events
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.answer, // This contains the markdown answer
        timestamp: Date.now(),
        ui_events: response.ui_events, // Include UI events for card rendering
        logs: response.logs, // Include logs for debugging/tracking
        metadata: response.metadata, // Include metadata
        model: null,
        tool: 'jira_rag', // only jira is used for now, but this allows for differentiation if we add more non-streaming RAG tools in the future
      };

      setState(prev => ({
        ...prev,
        chatHistory: [...prev.chatHistory, assistantMessage],
        isStreaming: false,
        currentResponse: '',
      }));
      dispatch(setIsStreaming(false));

      if (onComplete) {
        onComplete();
      }

    } catch (error: any) {
      logger.error('[JIRA HOOK] Error sending message:', error);
      
      const errorMessage = error.message || 'An error occurred while processing your request';
      
      setState(prev => ({
        ...prev,
        isStreaming: false,
        error: errorMessage,
        currentResponse: '',
      }));
      dispatch(setIsStreaming(false));

      if (options?.onError) {
        options.onError(error);
      }
    }
  }, [options, getService, dispatch, getOrCreateJiraSessionId]);

  /**
   * Cancel the current request
   */
  const cancelRequest = useCallback(() => {
    if (serviceRef.current) {
      logger.log('[JIRA HOOK] Canceling request');
      serviceRef.current.cancel();

      // Get the current streamed content before clearing
      const preservedContent = state.currentResponse || '';

      // Add stopped message to chat history if there was any content
      const stoppedMessage = preservedContent
        ? preservedContent + '\n\n*[Response was stopped by user.]*'
        : '*[Response was stopped by user.]*';

      setState(prev => {
        // Find the last user message to store as context for retry
        const lastUserMessage = [...prev.chatHistory].reverse().find(m => m.role === 'user');

        return {
          ...prev,
          isStreaming: false,
          currentResponse: '', // Clear current response since we're moving it to chat history
          chatHistory: [
            ...prev.chatHistory,
            {
              id: `${Date.now()}-${Math.random()}`,
              role: 'assistant',
              content: stoppedMessage,
              isStopped: true,
              failedUserMessage: lastUserMessage
            }
          ],
        };
      });
      dispatch(setIsStreaming(false));
    }
  }, [state.currentResponse, dispatch]);

  /**
   * Clear chat history and reset session
   */  
  const clearHistory = useCallback(() => {
    logger.log('[JIRA HOOK] Clearing history');
    // Reset the session ID so the next message starts a fresh backend conversation
    jiraSessionIdRef.current = '';
    logger.log('[JIRA HOOK] Jira session ID cleared');
    setState({
      chatHistory: [],
      currentResponse: '',
      isStreaming: false,
      error: null,
    });
    dispatch(setIsStreaming(false));
  }, [dispatch]);

  return {
    ...state,
    sendMessage,
    cancelRequest,
    clearHistory,
  };
}
